import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslator } from "@sia/style-guide";
import { DataCollectionContext } from "contexts/DataCollectionContext";
import { useOverheads } from "./SectionContent/OverheadsSection/useOverheads";
import { useEmployees } from "./SectionContent/EmployeesSection/useEmployees";
import { useUnsavedChangesGuard } from "./SectionContent/useUnsavedChangesGuard";
import { isEmployeeEntryFormDirty } from "./collectionData.helpers";

const SurveyFormSessionContext = createContext(null);

const genericDiscardModalCopy = {
  titleKey: "benchmarking.survey.save-form-warning-modal.title",
  leadKey: "benchmarking.survey.save-form-warning-modal.lead",
  cancelLabelKey: "modal.button.cancel",
  confirmLabelKey: "modal.button.discard-changes",
};

const employeeCancelModalCopy = {
  titleKey: "benchmarking.survey.employee.entry-form.confirm-close-form.title",
  leadKey: "benchmarking.survey.employee.entry-form.confirm-close-form",
  cancelLabelKey: "modal.button.cancel",
  confirmLabelKey: "modal.button.confirm",
};
const surveyTabHashes = ["#overhead-costs", "#hours-worked"];
const buildHash = (element) => `#${element?.id || element?.title || ""}`;
const getSurveyTabIndexFromHash = (hash) => {
  const index = surveyTabHashes.indexOf(hash);
  return index >= 0 ? index : null;
};

const isSamePathNavigation = (currentLocation, nextLocation) => currentLocation.pathname === nextLocation.pathname;
const getHashFromDestination = (destination) => {
  if (typeof destination === "string") {
    const hashIndex = destination.indexOf("#");
    return hashIndex >= 0 ? destination.slice(hashIndex) : "";
  }
  return destination?.hash || "";
};

/* eslint-disable react/prop-types */
export function SurveyFormSessionProvider({ children }) {
  const translate = useTranslator();
  const history = useHistory();
  const location = useLocation();
  const { showEmployeeEntryForm, employees } = useContext(DataCollectionContext);

  const overheadsForm = useOverheads();
  const employeeActions = useEmployees();
  const { employeeFormDirty, discardEmployeeFormChanges, openEntryForm, openFileUpload } = employeeActions;
  const { requestGuardedAction, DiscardChangesModal } = useUnsavedChangesGuard({ translate });
  const selectedSurveyTab = getSurveyTabIndexFromHash(location.hash) ?? 0;
  const employeeDirty = useMemo(
    () =>
      isEmployeeEntryFormDirty({
        employeeFormDirty,
        showEmployeeEntryForm,
      }),
    [employeeFormDirty, showEmployeeEntryForm]
  );

  const employeeDirtyRef = useRef(employeeDirty);
  const selectedSurveyTabRef = useRef(selectedSurveyTab);
  const pendingInternalNavigationRef = useRef(null);
  const pendingRouteNavigationRef = useRef(null);
  const resolvingInternalNavigationRef = useRef(false);
  const resolvingRouteNavigationRef = useRef(false);
  const allowNextInternalHashNavigationRef = useRef(false);
  const allowNextRouteNavigationRef = useRef(false);

  useEffect(() => {
    employeeDirtyRef.current = employeeDirty;
  }, [employeeDirty]);

  useEffect(() => {
    selectedSurveyTabRef.current = selectedSurveyTab;
  }, [selectedSurveyTab]);

  const currentTabDirty = selectedSurveyTab === 1 && employeeDirty;
  const hasAnyUnsavedChanges = employeeDirty;
  const isCollectionDataSubmitted = overheadsForm.isOverheadsComplete && employees.length > 0;

  useEffect(() => {
    const handler = (event) => {
      if (!hasAnyUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasAnyUnsavedChanges]);

  const cancelInternalNavigation = useCallback(() => {
    pendingInternalNavigationRef.current = null;
    resolvingInternalNavigationRef.current = false;
  }, []);

  const commitInternalNavigation = useCallback(() => {
    const pending = pendingInternalNavigationRef.current;
    pendingInternalNavigationRef.current = null;
    resolvingInternalNavigationRef.current = false;
    if (!pending) return;
    allowNextInternalHashNavigationRef.current = true;
    if (pending.location) {
      if (pending.action === "REPLACE") history.replace(pending.location);
      else history.push(pending.location);
      return;
    }
    if (pending.action === "REPLACE") history.replace(pending.to, pending.state);
    else history.push(pending.to, pending.state);
  }, [history]);

  const resolveInternalNavigation = useCallback(() => {
    if (resolvingInternalNavigationRef.current) return;
    resolvingInternalNavigationRef.current = true;
    const sourceTab = selectedSurveyTabRef.current;
    if (sourceTab === 1 && employeeDirtyRef.current) {
      requestGuardedAction({
        reason: "tab-switch",
        isDirty: true,
        onDiscard: discardEmployeeFormChanges,
        onContinue: commitInternalNavigation,
        onCancel: cancelInternalNavigation,
        modalCopy: genericDiscardModalCopy,
      });
      return;
    }
    commitInternalNavigation();
  }, [cancelInternalNavigation, commitInternalNavigation, discardEmployeeFormChanges, requestGuardedAction]);

  const navigateToSurveyTab = useCallback(
    (tabIndex, destination) => {
      if (tabIndex == null || tabIndex < 0) return;
      const targetHash = destination.hash || getHashFromDestination(destination.to || destination.location);
      const alreadyAtTarget = tabIndex === selectedSurveyTabRef.current && targetHash === location.hash && !pendingInternalNavigationRef.current;
      if (alreadyAtTarget) return;
      pendingInternalNavigationRef.current = { tabIndex, ...destination };
      resolveInternalNavigation();
    },
    [location.hash, resolveInternalNavigation]
  );

  const requestSurveyTabChange = useCallback(
    (index, element) => {
      const hash = buildHash(element);
      navigateToSurveyTab(index, {
        hash,
        to: `${location.pathname}${hash}`,
        action: "PUSH",
      });
    },
    [location.pathname, navigateToSurveyTab]
  );

  const topSectionHistory = useMemo(
    () => ({
      ...history,
      location,
      push: (to, state) => {
        const hash = getHashFromDestination(to);
        const nextTabIndex = getSurveyTabIndexFromHash(hash);
        if (nextTabIndex == null) {
          history.push(to, state);
          return;
        }
        navigateToSurveyTab(nextTabIndex, {
          hash,
          to,
          state,
          action: "PUSH",
        });
      },
    }),
    [history, location, navigateToSurveyTab]
  );

  const completeRouteNavigation = useCallback(() => {
    const pending = pendingRouteNavigationRef.current;
    pendingRouteNavigationRef.current = null;
    resolvingRouteNavigationRef.current = false;
    if (!pending) return;
    allowNextRouteNavigationRef.current = true;
    if (pending.action === "REPLACE") history.replace(pending.location);
    else history.push(pending.location);
  }, [history]);

  const cancelRouteNavigation = useCallback(() => {
    pendingRouteNavigationRef.current = null;
    resolvingRouteNavigationRef.current = false;
  }, []);

  const resolveRouteNavigation = useCallback(() => {
    if (resolvingRouteNavigationRef.current) return;
    resolvingRouteNavigationRef.current = true;
    if (employeeDirtyRef.current) {
      requestGuardedAction({
        reason: "route-leave",
        isDirty: true,
        onDiscard: discardEmployeeFormChanges,
        onContinue: completeRouteNavigation,
        onCancel: cancelRouteNavigation,
        modalCopy: genericDiscardModalCopy,
      });
      return;
    }

    completeRouteNavigation();
  }, [cancelRouteNavigation, completeRouteNavigation, discardEmployeeFormChanges, requestGuardedAction]);

  useEffect(() => {
    const unblock = history.block((nextLocation, action) => {
      if (allowNextRouteNavigationRef.current) {
        allowNextRouteNavigationRef.current = false;
        return true;
      }
      const isSamePath = isSamePathNavigation(location, nextLocation);
      const nextTabIndex = getSurveyTabIndexFromHash(nextLocation.hash);
      const isSurveyTabHashNavigation = isSamePath && nextTabIndex != null && nextTabIndex !== selectedSurveyTabRef.current;
      if (isSurveyTabHashNavigation) {
        if (allowNextInternalHashNavigationRef.current) {
          allowNextInternalHashNavigationRef.current = false;
          return true;
        }
        navigateToSurveyTab(nextTabIndex, {
          hash: nextLocation.hash,
          location: nextLocation,
          action,
        });
        return false;
      }
      if (isSamePath) return true;
      if (!hasAnyUnsavedChanges) return true;
      if (pendingRouteNavigationRef.current) return false;
      pendingRouteNavigationRef.current = {
        location: nextLocation,
        action,
      };
      resolveRouteNavigation();
      return false;
    });
    return () => unblock();
  }, [hasAnyUnsavedChanges, history, location, navigateToSurveyTab, resolveRouteNavigation]);

  const handleEmployeeCancel = useCallback(() => {
    requestGuardedAction({
      reason: "employee-cancel",
      isDirty: employeeDirty,
      onDiscard: discardEmployeeFormChanges,
      onCleanContinue: discardEmployeeFormChanges,
      modalCopy: employeeCancelModalCopy,
    });
  }, [discardEmployeeFormChanges, employeeDirty, requestGuardedAction]);

  const handleRequestOpenEmployeeForm = useCallback(
    (employee, employeeId) => {
      requestGuardedAction({
        reason: "employee-edit-another",
        isDirty: employeeDirty,
        onDiscard: discardEmployeeFormChanges,
        onContinue: () => openEntryForm(employee, employeeId),
        onCleanContinue: () => openEntryForm(employee, employeeId),
        modalCopy: employeeCancelModalCopy,
      });
    },
    [discardEmployeeFormChanges, employeeDirty, openEntryForm, requestGuardedAction]
  );

  const handleRequestOpenFileUpload = useCallback(() => {
    requestGuardedAction({
      reason: "employee-open-import",
      isDirty: employeeDirty,
      onDiscard: discardEmployeeFormChanges,
      onContinue: openFileUpload,
      onCleanContinue: openFileUpload,
      modalCopy: employeeCancelModalCopy,
    });
  }, [discardEmployeeFormChanges, employeeDirty, openFileUpload, requestGuardedAction]);

  const discardCurrentTabChanges = useCallback(() => {
    if (selectedSurveyTab === 1) discardEmployeeFormChanges();
  }, [discardEmployeeFormChanges, selectedSurveyTab]);
  const discardAllSurveyDraftChanges = useCallback(() => {
    if (employeeDirty) discardEmployeeFormChanges();
  }, [discardEmployeeFormChanges, employeeDirty]);

  const value = useMemo(
    () => ({
      selectedSurveyTab,
      requestSurveyTabChange,
      topSectionHistory,
      overheadsForm,
      employeeActions,
      handleEmployeeCancel,
      handleRequestOpenEmployeeForm,
      handleRequestOpenFileUpload,
      employeeDirty,
      currentTabDirty,
      hasAnyUnsavedChanges,
      isCollectionDataSubmitted,
      discardCurrentTabChanges,
      discardAllSurveyDraftChanges,
    }),
    [
      currentTabDirty,
      discardAllSurveyDraftChanges,
      discardCurrentTabChanges,
      employeeActions,
      employeeDirty,
      handleEmployeeCancel,
      handleRequestOpenEmployeeForm,
      handleRequestOpenFileUpload,
      hasAnyUnsavedChanges,
      isCollectionDataSubmitted,
      overheadsForm,
      requestSurveyTabChange,
      selectedSurveyTab,
      topSectionHistory,
    ]
  );

  return (
    <SurveyFormSessionContext.Provider value={value}>
      {children}
      <DiscardChangesModal />
    </SurveyFormSessionContext.Provider>
  );
}

export function useSurveyFormSession() {
  const context = useContext(SurveyFormSessionContext);
  if (!context) throw new Error("useSurveyFormSession must be used inside SurveyFormSessionProvider");
  return context;
}
